import type { GridApiPremium } from "@mui/x-data-grid-premium";
import { gridRowSelector } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import type { ApiError } from "@entrilia/shared/api/types";
import type { StateAction } from "@entrilia/shared/types";
import type { FileInfo } from "../../../api/types/fileTypes";

export interface FilesState {
  uploadingFiles: FilesRowModel[];
  searchTerm: string;
}

export interface FilesRowModel extends FileInfo {
  uploadState?: UploadFileState;
}

export interface UploadFileState {
  status: UploadFileStatus;
  validationError?: string;
  uploadError?: ApiError;
}

type UploadFileStatus = "in_progress" | "completed" | "error";

export const getInitialFilesState = (): FilesState => ({
  uploadingFiles: [],
  searchTerm: "",
});

// Actions

export const searchFilesAction =
  (searchTerm: string): StateAction<FilesState> =>
  (state) => {
    return {
      ...state,
      searchTerm,
    };
  };

export const removeUploadingFileAction =
  (catalogueId: string): StateAction<FilesState> =>
  (state) => {
    return {
      ...state,
      uploadingFiles: state.uploadingFiles.filter((file) => file.catalogueId !== catalogueId),
    };
  };

export const startUploadingFilesAction =
  (uploadingFileModels: FilesRowModel[]): StateAction<FilesState> =>
  (state) => {
    return {
      ...state,
      uploadingFiles: uploadingFileModels,
    };
  };

export const updateFileUploadStateAction =
  (catalogueId: string, uploadState: UploadFileState): StateAction<FilesState> =>
  (state) => {
    return {
      ...state,
      uploadingFiles: state.uploadingFiles.map((fileModel) =>
        fileModel.catalogueId === catalogueId ? { ...fileModel, uploadState } : fileModel
      ),
    };
  };

export const clearCompletedUploadsAction = (): StateAction<FilesState> => (state) => {
  return {
    ...state,
    uploadingFiles: state.uploadingFiles.filter((file) => file.uploadState?.status !== "completed"),
  };
};

// API Helpers

export const renameFileInGrid = (
  catalogueId: string,
  newFileName: string,
  apiRef: RefObject<GridApiPremium | null>
) => {
  const file = gridRowSelector(apiRef, catalogueId) as FileInfo | undefined;
  if (!file) {
    return;
  }

  const fileUpdate = { ...file, fileName: newFileName };
  apiRef.current?.updateRows([fileUpdate]);
};
